import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings, BrandLogo } from "../context/SettingsContext";
import { ArrowRight } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      const dest = loc.state?.from?.pathname || "/dashboard";
      nav(dest, { replace: true });
    } else setErr(r.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Inline SVG filter: keeps cool blue/teal tones (solar panels) and desaturates
          warmer tones (sky, walls, vegetation). Single declarative pass — no image
          editing needed. The matrix routes R+G through a luminance-style grayscale
          while preserving the blue channel almost in full. */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
      >
        <defs>
          <filter id="panels-only-color" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="
                0.36 0.50 0.14 0 0
                0.36 0.50 0.14 0 0
                0.04 0.05 0.91 0 0
                0    0    0    1 0
              "
            />
          </filter>
        </defs>
      </svg>

      <div className="relative hidden lg:block">
        <img
          src={BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(1.08) contrast(1.03)" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,214,223,0.20) 0%, rgba(49,64,65,0.30) 45%, rgba(49,64,65,0.68) 100%)" }}
        />
        <div className="absolute inset-0 p-12 flex flex-col justify-end text-white">
          <div>
            <div className="label !text-white/70 !text-[10px] mb-4">Design · Quote · Install · Monitor</div>
            <div className="font-display text-5xl font-bold leading-[1.05]">
              The control room<br />for UK solar PV.
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm appear" data-testid="login-form">
          <div className="flex items-center gap-3 mb-10" data-testid="login-brand">
            <BrandLogo size={32} />
            <div>
              <div className="font-display text-xl font-bold leading-none uppercase tracking-wide text-[var(--ink)]">
                {settings?.org_name || "Flow"}
              </div>
            </div>
          </div>

          <div className="label mb-3">Sign in</div>
          <h1 className="font-display text-4xl font-bold mb-8">Welcome back.</h1>

          <div className="space-y-5">
            <div>
              <label className="label block mb-1">Email</label>
              <input
                data-testid="login-email-input"
                className="input-swiss"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="label block mb-1">Password</label>
              <input
                data-testid="login-password-input"
                className="input-swiss"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {err && (
              <div className="text-xs text-[var(--err)] font-mono" data-testid="login-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center"
              data-testid="login-submit-btn"
            >
              {busy ? "Authenticating…" : "Continue"}
              {!busy && <ArrowRight size={14} />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
